import type { Book } from "@/data/books";
import { emotionalTagsFor } from "@/lib/recommendations";

export interface DnaSlice {
  label: string;
  percent: number;
  color: string; // tailwind bg color class
}

const PALETTE = [
  "bg-rose-500",
  "bg-purple-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-sky-500",
  "bg-fuchsia-500",
];

/**
 * Build a Book DNA breakdown from the books a reader has saved/read,
 * plus their own reactions (each reaction reinforces an affinity bucket).
 */
export const buildBookDna = (
  affinityBooks: Book[],
  ownReactionTallies: Record<string, number> = {},
): DnaSlice[] => {
  const tally: Record<string, number> = {};
  const bump = (k: string, n = 1) => {
    if (!k) return;
    tally[k] = (tally[k] ?? 0) + n;
  };

  for (const b of affinityBooks) {
    for (const tag of emotionalTagsFor(b)) bump(tag);
    if (b.emotionalIntensity === "devastating") bump("Emotional Devastation", 2);
    if (b.emotionalIntensity === "light") bump("Cozy Romance", 1);
    for (const t of b.tropes ?? []) {
      const tl = t.toLowerCase();
      if (/possessive|alpha|dominant/.test(tl)) bump("Possessive MMCs");
      if (/slow burn/.test(tl)) bump("Slow Burn");
      if (/healing|hurt\/comfort|grovel/.test(tl)) bump("Healing Romance");
      if (/forbidden|enemies|rivals/.test(tl)) bump("Forbidden Tension");
      if (/mafia|dark/.test(tl)) bump("Dark Romance");
      if (/morally gray|anti-hero|villain/.test(tl)) bump("Morally Gray");
    }
  }

  // Reactions reinforce certain buckets
  const r = (k: string) => ownReactionTallies[k] ?? 0;
  bump("Emotional Devastation", r("CRYING") + r("EMOTIONALLY DESTROYED") + r("NEED THERAPY"));
  bump("Possessive MMCs", r("TOUCH-HER-AND-DIE") + r("UNWELL OVER THIS MMC"));
  bump("Slow Burn", r("SLOW BURN TORTURE"));
  bump("Cozy Romance", r("KICKING MY FEET"));
  bump("Forbidden Tension", r("OBSESSED") + r("SCREAMING"));

  const total = Object.values(tally).reduce((a, b) => a + b, 0);
  if (!total) return [];

  const sorted = Object.entries(tally)
    .sort((a, z) => z[1] - a[1])
    .slice(0, 4);
  const sortedTotal = sorted.reduce((a, [, v]) => a + v, 0);

  return sorted.map(([label, v], i) => ({
    label,
    percent: Math.max(5, Math.round((v / sortedTotal) * 100)),
    color: PALETTE[i % PALETTE.length],
  }));
};

export const REACTIONS = [
  { id: "SCREAMING", emoji: "😱" },
  { id: "CRYING", emoji: "😭" },
  { id: "OBSESSED", emoji: "🔥" },
  { id: "NEED THERAPY", emoji: "🛋️" },
  { id: "KICKING MY FEET", emoji: "🦋" },
  { id: "TOUCH-HER-AND-DIE", emoji: "🗡️" },
  { id: "EMOTIONALLY DESTROYED", emoji: "💔" },
  { id: "SLOW BURN TORTURE", emoji: "⏳" },
  { id: "UNWELL OVER THIS MMC", emoji: "💀" },
] as const;

export type ReactionId = typeof REACTIONS[number]["id"];
